/**
 * The decisions behind the camera panel, apart from the panel.
 *
 * <p>Everything here can be got wrong in a way nobody would see until an auditor's video
 * was refused or filled the limit in fifteen seconds. The panel itself is markup around
 * these, and there is no component-testing stack in this project to render it with — so
 * the part that decides goes where it can be asserted, and the part that draws stays in
 * the component.</p>
 */

/**
 * Capped at 1080p, deliberately.
 *
 * <p>Phones default to 4K and a phone recording 4K writes about seven megabytes a second,
 * so the hundred-megabyte ceiling arrives fifteen seconds in — mid-sentence, with the
 * auditor holding the phone up at a shelf. At 1080p and four megabits the same hundred
 * megabytes is over three minutes, which is longer than anybody records a stockroom.</p>
 *
 * <p><b>`max`, not `ideal` alone.</b> Ideal is a preference a device may ignore upward, and
 * a device that ignores it upward is the device that caused this.</p>
 */
export const CAPTURE_CONSTRAINTS: MediaStreamConstraints = {
  video: {
    facingMode: { ideal: 'environment' },
    width: { ideal: 1920, max: 1920 },
    height: { ideal: 1080, max: 1080 },
  },
  audio: true,
};

/** About four megabits: a touch over thirty megabytes a minute at 1080p. */
export const VIDEO_BITS_PER_SECOND = 4_000_000;

/**
 * What to record in, best first.
 *
 * <p>Safari records MP4 and nothing else; Chrome and Firefox record WebM. The server
 * accepts all three names and compares the type without its parameters, which is what makes
 * `video/webm;codecs=vp9,opus` land as a WebM rather than as something unrecognised.</p>
 */
export const RECORDING_TYPES = [
  'video/mp4',
  'video/webm;codecs=vp9,opus',
  'video/webm;codecs=vp8,opus',
  'video/webm',
];

/**
 * The best type this browser will record, or an empty string to let it choose.
 *
 * <p>Takes the predicate rather than reaching for `MediaRecorder` so the order can be
 * asserted without a browser. Empty rather than throwing: a browser that supports none of
 * these still records something, and whatever it produces has a better chance of being
 * accepted than nothing does.</p>
 */
export const pickRecordingType = (isSupported: (type: string) => boolean) =>
  RECORDING_TYPES.find(isSupported) ?? '';

/**
 * Whether the page may open a camera itself.
 *
 * <p>Two conditions, and the first is the one that bites. `navigator.mediaDevices` does not
 * exist outside a secure context, and the development server is plain http on a LAN address
 * — measured, not assumed: `isSecureContext` is false there and `mediaDevices` is
 * undefined. A build that only knew how to capture this way would have no camera at all on
 * dev, nor on any installation put behind http.</p>
 *
 * <p>Which is why the caller keeps the `capture` file inputs as the other path rather than
 * deleting them: this is the better camera where it is allowed to run, and the file input's
 * camera is the one that still works where it is not.</p>
 */
export const canOpenCamera = () =>
  typeof window !== 'undefined' &&
  window.isSecureContext &&
  typeof navigator?.mediaDevices?.getUserMedia === 'function';

/**
 * Which sentence to show when the camera will not open.
 *
 * <p>Three, not one, because they ask different things of the person reading. A refusal is
 * a decision they can change in the address bar; a missing device is something they have to
 * find; anything else is neither, and promising a remedy that does not exist is worse than
 * admitting it did not work.</p>
 */
export function cameraErrorKey(name: string) {
  if (name === 'NotAllowedError' || name === 'SecurityError') return 'audit.camera.denied';
  if (name === 'NotFoundError' || name === 'OverconstrainedError') return 'audit.camera.none';

  return 'audit.camera.failed';
}
